import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "results", "filterForm", "query", "professionFilter", "attributeFilter", "page"]

  open(event) {
    const inputId = event.currentTarget.dataset.skillPickerSlotInputId
    this.activeInput = document.getElementById(inputId)
    this.activeSlot = this.activeInput?.closest("[data-build-skill-order-target='slot']")
    const character = this.activeInput?.closest("[data-build-editor-character]")
    this.activeProfessions = [
      character?.querySelector("[data-build-editor-target='primaryProfession']")?.value,
      character?.querySelector("[data-build-editor-target='secondaryProfession']")?.value
    ].filter((id) => Number(id) > 0).map(Number)
    this.queryTarget.value = ""
    this.professionFilterTarget.value = ""
    this.attributeFilterTarget.value = ""
    this.pageTarget.value = "1"
    this.dialogTarget.showModal()
    this.fetchResults()
    this.queryTarget.focus()
  }

  close() {
    this.dialogTarget.close()
  }

  search(event) {
    event.preventDefault()
    this.pageTarget.value = "1"
    this.fetchResults()
  }

  browseAll(event) {
    event.preventDefault()
    this.queryTarget.value = ""
    this.professionFilterTarget.value = ""
    this.attributeFilterTarget.value = ""
    this.pageTarget.value = "1"
    this.fetchResults()
  }

  paginate(event) {
    const link = event.target.closest("a[href]")
    if (!link) return

    event.preventDefault()
    this.fetchResults(new URL(link.href, window.location.href).searchParams)
  }

  async fetchResults(searchParams = new URLSearchParams(new FormData(this.filterFormTarget))) {
    const requestId = (this.requestId || 0) + 1
    this.requestId = requestId
    const url = new URL(this.filterFormTarget.action, window.location.href)
    url.search = searchParams.toString()
    this.resultsTarget.setAttribute("aria-busy", "true")
    this.resultsTarget.innerHTML = '<p class="text-sm text-muted" role="status">Loading skills…</p>'

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error(`Skill catalog request failed (${response.status})`)
      const html = await response.text()
      if (requestId !== this.requestId) return

      const document = new DOMParser().parseFromString(html, "text/html")
      const responseFrame = document.getElementById(this.resultsTarget.id)
      if (!responseFrame) throw new Error("Skill catalog response did not contain its results frame")

      this.resultsTarget.innerHTML = responseFrame.innerHTML
      this.resultsTarget.dataset.loaded = "true"
      this.resultsTarget.removeAttribute("aria-busy")
      this.updateEligibility()
    } catch (_error) {
      if (requestId !== this.requestId) return

      this.resultsTarget.removeAttribute("aria-busy")
      this.resultsTarget.innerHTML = '<p class="text-sm text-red-300" role="alert">Could not load the skill catalog. Try again.</p>'
    }
  }

  choose(event) {
    const result = event.currentTarget
    if (!this.activeInput || result.disabled) return

    this.activeInput.value = result.dataset.skillId
    this.activeInput.dataset.skillProfessionId = result.dataset.professionId
    this.activeInput.dataset.skillAttributeId = result.dataset.attributeId
    this.activeInput.dataset.skillPve = result.dataset.pve
    const resultImage = result.querySelector("img")
    this.activeSlot.querySelectorAll("img").forEach((image) => {
      image.src = resultImage.currentSrc || resultImage.src
      image.title = result.dataset.skillName
    })
    this.activeSlot.querySelector("[data-skill-picker-target='name']").textContent = result.dataset.skillName
    this.activeSlot.querySelector("[data-skill-picker-target='removeButton']").hidden = false
    this.updateSlotLabel(this.activeSlot, result.dataset.skillName)
    this.activeInput.dispatchEvent(new Event("input", { bubbles: true }))
    this.activeInput.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  remove(event) {
    this.activeInput = document.getElementById(event.currentTarget.dataset.skillPickerSlotInputId)
    this.activeSlot = this.activeInput?.closest("[data-build-skill-order-target='slot']")
    if (!this.activeInput || !this.activeSlot) return

    this.activeInput.value = "0"
    delete this.activeInput.dataset.skillProfessionId
    delete this.activeInput.dataset.skillAttributeId
    delete this.activeInput.dataset.skillPve
    this.activeSlot.querySelectorAll("img").forEach((image) => {
      image.src = this.activeSlot.dataset.emptyIconSrc
      image.title = "Empty"
    })
    this.activeSlot.querySelector("[data-skill-picker-target='name']").textContent = "Empty"
    this.activeSlot.querySelector("[data-skill-picker-target='removeButton']").hidden = true
    this.updateSlotLabel(this.activeSlot, "Empty")
    this.activeInput.dispatchEvent(new Event("input", { bubbles: true }))
    this.activeInput.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateEligibility() {
    this.resultsTarget.dataset.loaded = "true"
    this.resultsTarget.querySelectorAll("button[data-skill-id]").forEach((result) => {
      const professionId = Number(result.dataset.professionId)
      const eligible = result.dataset.pve === "true" || professionId === 0 || this.activeProfessions?.includes(professionId)
      result.disabled = !eligible
      result.setAttribute("aria-disabled", String(!eligible))
    })
  }

  updateSlotLabel(slot, name) {
    const slots = Array.from(slot.parentElement.children)
    const index = slots.indexOf(slot) + 1
    slot.dataset.skillName = name
    slot.setAttribute("aria-label", `Skill slot ${index}: ${name}; use left and right arrow keys to reorder`)
    const chooseButton = slot.querySelector("[data-skill-picker-target='chooseButton']")
    chooseButton.setAttribute(
      "aria-label",
      name === "Empty" ? `Choose a skill for slot ${index}` : `Replace skill in slot ${index}: ${name}`
    )
  }
}
